import React, { useCallback, useState, useRef, useMemo, useEffect, memo } from "react";
import { Tooltip } from "antd";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import "katex/dist/katex.min.css";
import type { ISegment, IToolTipTool, StopType } from "@typing/wsui/getMessages";
import type { ToolClickHandler } from "../../types";
import { getToolIcon, IconState } from "../../utils";
import FeedbackModal from "@components/FeedbackModal";
import { useDebouncedCallback } from "@hooks/useDebouncedCallback";
import { useReferenceStore } from "@store/referenceStore";
import styles from "./index.module.less";
import { useUbcSend } from "@hooks/useUbcSend";
import type { IThinkReference } from "../ThinkingProcessV2";

// ==================== 模块级常量：避免每次渲染创建新数组 ====================
const REMARK_PLUGINS = [
    [remarkGfm, { singleTilde: false }], // 禁用单波浪线删除线，只支持 ~~text~~
    remarkBreaks,
    remarkMath,
] as any;
const REHYPE_PLUGINS = [rehypeKatex, rehypeRaw] as any;

interface IMarkdownContentProps {
    segment: ISegment;
    messageId: string;
    sessionId: string;
    onToolClick: ToolClickHandler;
    stopType?: StopType; // 停止类型（用于视图层处理内容追加）
    references?: IThinkReference[]; // 当前消息的引用文献列表，用于查找 reference_id
    sseLoading?: boolean; // SSE 加载中，禁用复制、赞踩按钮
}

/**
 * 表格包装器组件 - 使用 memo 阻止不必要的重渲染，保持滚动位置
 */
interface TableWrapperProps {
    children: React.ReactNode;
    className: string;
}

const TableWrapper = memo<TableWrapperProps>(({ children, className }) => <div className={className}>{children}</div>);

/**
 * 增量 Markdown 渲染：
 * - stableContent：流式过程中已完成的段落（最后一个 \n\n 以上），用 memo 组件缓存，仅在段落完成时重解析
 * - streamingTail：当前正在输入的最后不完整段落，内容短，解析快
 * 相比整体渲染，可将 ReactMarkdown O(total_length) 解析降低为 O(tail_length)，消除长内容时的 50ms 卡顿
 */
interface StableMarkdownProps {
    content: string;
    components: Record<string, React.ComponentType<any>>;
}

const StableMarkdown = memo<StableMarkdownProps>(
    ({ content, components }) => (
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={components}>
            {content}
        </ReactMarkdown>
    ),
    (prev, next) => prev.content === next.content && prev.components === next.components
);

/**
 * Markdown 内容组件
 * SSE 流式数据已经是逐字推送，直接渲染即可
 */
const MarkdownContent: React.FC<IMarkdownContentProps> = ({
    segment,
    messageId,
    sessionId,
    onToolClick,
    stopType,
    references,
    sseLoading,
}) => {
    const { ubcSend } = useUbcSend();
    const contentRef = useRef<HTMLDivElement>(null);
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [feedbackType, setFeedbackType] = useState<"like" | "unlike">("like");
    const [hoveredTool, setHoveredTool] = useState<string | null>(null);

    // 获取引用文献 store 的 openDrawer 方法
    const { openDrawer } = useReferenceStore();

    // 处理点击事件，检测是否点击了引用索引
    const handleContentClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;

            // 检查是否点击了 ref-index 类的元素
            if (target.classList.contains("ref-index")) {
                e.preventDefault();
                e.stopPropagation();

                // 从 data 属性获取索引
                const index = target.dataset.index || undefined;
                // list 可能是逗号分隔的多个值，取第一个作为 paragraphIndex
                const listValue = target.dataset.list || "";
                const paragraphIndex = listValue ? parseInt(listValue.split(",")[0], 10) : undefined;

                // 打开引用文献抽屉
                openDrawer(sessionId, messageId, index, paragraphIndex);
                const ref_id = index ? references?.find((r) => String(r.index) === index)?.id : undefined;
                ubcSend({
                    type: "clk",
                    value: "paragraph_ref",
                    ext: {
                        product_info: {
                            session_id: sessionId,
                            message_id: messageId,
                            reference_id: ref_id,
                        },
                    },
                });
            }
        },
        [openDrawer, sessionId, messageId, references]
    );

    // 获取图标状态
    const getIconState = useCallback(
        (tool: IToolTipTool): IconState => {
            if (tool.status === 1) return "selected";
            if (hoveredTool === tool.type) return "hover";
            return "default";
        },
        [hoveredTool]
    );

    // 获取 Tooltip 文案
    const getTooltipTitle = useCallback((toolType: string): string => {
        const tooltipMap: Record<string, string> = {
            like: "满意",
            unlike: "不满意",
            copy: "复制",
        };
        return tooltipMap[toolType] || "";
    }, []);

    // 处理工具点击（使用自定义 Hook 创建防抖函数）
    const handleToolClick = useDebouncedCallback(
        (toolType: string, isSelected: boolean) => {
            // 如果已经选中,触发取消操作
            if (isSelected && (toolType === "like" || toolType === "unlike")) {
                onToolClick({
                    type: "cancel",
                    source: "markdown",
                    params: {
                        messageId,
                        fragmentId: segment.segment_idx,
                    },
                });
                return;
            }

            if (toolType === "like") {
                setFeedbackType("like");
                setFeedbackModalOpen(true);
                // 立即调用 API（不带 tags 和 content）
                onToolClick({
                    type: "like",
                    source: "markdown",
                    params: {
                        messageId,
                        fragmentId: segment.segment_idx,
                    },
                });
            } else if (toolType === "unlike") {
                setFeedbackType("unlike");
                setFeedbackModalOpen(true);
                // 立即调用 API（不带 tags 和 content）
                onToolClick({
                    type: "unlike",
                    source: "markdown",
                    params: {
                        messageId,
                        fragmentId: segment.segment_idx,
                    },
                });
            } else if (toolType === "copy") {
                // 从渲染后的 DOM 中提取纯文本
                if (contentRef.current) {
                    let copyContent = contentRef.current.innerText || contentRef.current.textContent;
                    onToolClick({ type: "copy", content: copyContent || "" });
                }
            }
        },
        1000,
        [messageId, segment.segment_idx, onToolClick]
    );

    // 统一的点击处理函数（关闭 Tooltip + 执行业务逻辑）
    const handleButtonClick = useCallback(
        (toolType: string, isSelected: boolean) => {
            // 点击时立即关闭 Tooltip
            setHoveredTool(null);

            // 执行业务逻辑
            handleToolClick(toolType, isSelected);

            ubcSend({
                type: "clk",
                value: isSelected ? `chat_${toolType}_cancel` : `chat_${toolType}`,
                ext: {
                    product_info: {
                        session_id: sessionId,
                        message_id: messageId,
                    },
                },
            });
        },
        [handleToolClick, messageId, sessionId]
    );

    // 渲染工具按钮
    const renderToolButton = useCallback(
        (tool: IToolTipTool, onClick: () => void) => {
            const iconState = getIconState(tool);
            const isSelected = tool.status === 1;

            const handleClick = () => {
                // 点击时立即关闭 Tooltip
                setHoveredTool(null);
                onClick();
            };

            return (
                <Tooltip
                    title={getTooltipTitle(tool.type)}
                    placement="top"
                    arrow={false}
                    open={hoveredTool === tool.type ? undefined : false}
                >
                    <button
                        className={`${styles.actionBtn} ${isSelected ? styles.selected : ""} ${sseLoading ? styles.disabled : ""}`}
                        onClick={handleClick}
                        onMouseEnter={() => setHoveredTool(tool.type)}
                        onMouseLeave={() => setHoveredTool(null)}
                        disabled={sseLoading}
                    >
                        <img src={getToolIcon(tool.type, iconState)} alt={tool.type} />
                    </button>
                </Tooltip>
            );
        },
        [getIconState, getTooltipTitle, hoveredTool, sseLoading]
    );

    // 提交反馈（使用自定义 Hook 创建防抖函数）
    const handleFeedbackSubmit = useDebouncedCallback(
        (type: "like" | "unlike", selectedValues: string[], inputValue: string) => {
            onToolClick({
                type,
                source: "markdown",
                params: {
                    messageId,
                    fragmentId: segment.segment_idx,
                    tags: selectedValues,
                    content: inputValue,
                },
            });
        },
        1000,
        [messageId, segment.segment_idx, onToolClick]
    );

    /**
     * 处理显示内容
     * 1. 如果用户主动停止（stopType 存在）：追加 "...." 表示内容被截断
     * 2. 如果正在流式输出（未完成且非停止）：追加 shade 遮罩显示打字效果
     * 3. 其他情况：显示原始内容
     */
    const displayContent = useMemo(() => {
        if (stopType) {
            return segment.content + "...";
        }
        if (!segment.is_finished) {
            return `${segment.content}<span class="${styles.shade}">&nbsp;</span>`;
        }
        return segment.content;
    }, [segment.content, segment.is_finished, stopType]);

    // 创建 table 组件渲染函数
    const renderTable = useCallback(({ children, ...props }: any) => {
        return (
            <TableWrapper className={styles.tableWrapper}>
                <table {...props}>{children}</table>
            </TableWrapper>
        );
    }, []);

    // 稳定的 components 对象引用，避免每次渲染 ReactMarkdown 重新解析
    const markdownComponents = useMemo(
        () => ({
            table: renderTable,
            a: ({ children, ...props }: any) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                    {children}
                </a>
            ),
        }),
        [renderTable]
    );

    /**
     * 增量渲染切分：在最后一个段落边界（\n\n）处切分 displayContent
     * - stableContent：已完成的段落，传给 StableMarkdown（memo 缓存，段落完成才重解析）
     * - streamingTail：当前不完整段落，内容短，直接用 ReactMarkdown 渲染
     * 流式阶段：只有 tail 在变化，stable 部分不触发重解析
     * 完成阶段：is_finished=true，displayContent 不再变化，切分结果也稳定
     */
    const { stableContent, streamingTail } = useMemo(() => {
        const lastBreak = displayContent.lastIndexOf("\n\n");
        if (lastBreak < 0) {
            return { stableContent: "", streamingTail: displayContent };
        }
        return {
            stableContent: displayContent.slice(0, lastBreak + 2),
            streamingTail: displayContent.slice(lastBreak + 2),
        };
    }, [displayContent]);

    useEffect(() => {
        if (segment.is_finished) {
            // 工具按钮曝光
            if (segment.tool_tip?.tools && segment.tool_tip.tools.length > 0) {
                segment.tool_tip.tools.map((item) => {
                    ubcSend({
                        type: "view",
                        value: `chat_${item.type}`,
                        ext: {
                            product_info: {
                                session_id: sessionId,
                                message_id: messageId,
                            },
                        },
                    });
                });
            }
            // 引用角标曝光
            const refElements = contentRef.current?.querySelectorAll(".ref-index");
            if (refElements?.length) {
                refElements.forEach((el) => {
                    const index = (el as HTMLElement).dataset.index;
                    const refId = index ? references?.find((r) => String(r.index) === index)?.id : undefined;
                    ubcSend({
                        type: "view",
                        value: "paragraph_ref",
                        ext: {
                            product_info: {
                                session_id: sessionId,
                                message_id: messageId,
                                reference_id: refId,
                                reference_index: index,
                            },
                        },
                    });
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageId, segment.is_finished, sessionId]);

    return (
        <div>
            <div ref={contentRef} className={styles.markdownContent} onClick={handleContentClick}>
                {/* 增量渲染：stable 段落用 memo 缓存，只有 tail 每次重解析 */}
                {stableContent && <StableMarkdown content={stableContent} components={markdownComponents} />}
                <ReactMarkdown
                    remarkPlugins={REMARK_PLUGINS}
                    rehypePlugins={REHYPE_PLUGINS}
                    components={markdownComponents}
                >
                    {streamingTail}
                </ReactMarkdown>
            </div>
            {stopType && <div className={styles.stopNotice}>回答已终止</div>}
            {segment.is_finished && !stopType && segment.tool_tip?.tools && segment.tool_tip.tools.length > 0 && (
                <div className={styles.actions}>
                    {segment.tool_tip.tools.map((tool) => {
                        const isSelected = tool.status === 1;
                        if (tool.type === "like" || tool.type === "unlike") {
                            return (
                                <FeedbackModal
                                    key={tool.type}
                                    open={feedbackModalOpen && feedbackType === tool.type}
                                    type={tool.type}
                                    configType="markdown"
                                    messageId={messageId}
                                    popupConfig={tool.popupConfig}
                                    onClose={() => setFeedbackModalOpen(false)}
                                    onSubmit={handleFeedbackSubmit}
                                >
                                    {renderToolButton(tool, () => handleButtonClick(tool.type, isSelected))}
                                </FeedbackModal>
                            );
                        }
                        return (
                            <Tooltip
                                key={tool.type}
                                title={getTooltipTitle(tool.type)}
                                placement="top"
                                arrow={false}
                                open={hoveredTool === tool.type ? undefined : false}
                            >
                                <button
                                    className={`${styles.actionBtn} ${sseLoading ? styles.disabled : ""}`}
                                    onClick={() => handleButtonClick(tool.type, isSelected)}
                                    onMouseEnter={() => setHoveredTool(tool.type)}
                                    onMouseLeave={() => setHoveredTool(null)}
                                    disabled={sseLoading}
                                >
                                    <img
                                        src={getToolIcon(tool.type, hoveredTool === tool.type ? "hover" : "default")}
                                        alt={tool.type}
                                    />
                                </button>
                            </Tooltip>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default memo(MarkdownContent);
